/**
 * Energy availability (EA) card — surfaces the backend that has been running
 * headless since Phase 22.3.8.
 *
 * Backend contract (do not widen without re-reading the router):
 *   GET /api/energy-availability/window  — no params. Fixed trailing 7 days,
 *       end date chosen server-side from User.timezone. Returns per-day
 *       readings plus the two flags the meal-plan generator already acts on.
 *   GET /api/energy-availability/today   — no params. Same day as readings[0],
 *       but it is the ONLY endpoint that returns ffm_kg, which the methodology
 *       panel needs to be honest about what the divisor was. Fetched lazily,
 *       only when that panel is opened.
 *
 * EA = (intake_kcal − exercise_kcal) / FFM_kg. It is not a calorie balance and
 * not a deficit: it is what is left for everything the body does that is not
 * exercise. Thresholds are read off the service, not invented here —
 * EA_SUBCLINICAL_THRESHOLD = 30 is the operative line, 45 the aspiration, 25
 * the floor (services/energy_availability.py:50-52).
 *
 * Deliberately descriptive. The app has an estimated intake, a TSS-derived
 * expenditure guess and a bioimpedance FFM figure; that is enough to say "this
 * pattern is associated with X, worth a conversation with a clinician" and not
 * enough to say "you have RED-S".
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { AlertTriangle, Gauge, Info, RefreshCw, Utensils } from "lucide-react";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { api } from "@/api/client";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/dates";

/* ------------------------------------------------------------------ types */

type EAStatus = "optimal" | "subclinical" | "problematic" | "severe";

interface EAReading {
  date: string;
  intake_kcal: number;
  exercise_kcal: number;
  ea_kcal_per_kg_ffm: number;
  status: EAStatus;
  has_meal_data: boolean;
}

interface EAWindow {
  end_date: string;
  rolling_3d_avg: number | null;
  rolling_7d_avg: number | null;
  days_below_30_in_last_7: number;
  days_below_25_in_last_7: number;
  days_with_data: number;
  chronic_low_ea: boolean;
  severe_low_ea: boolean;
  insufficient_data: boolean;
  readings: EAReading[];
}

/** /today adds ffm_kg on top of the per-reading shape. */
interface EAToday extends EAReading {
  ffm_kg: number;
}

async function getEnergyWindow(): Promise<EAWindow> {
  const { data } = await api.get<EAWindow>("/energy-availability/window");
  return data;
}

async function getEnergyToday(): Promise<EAToday> {
  const { data } = await api.get<EAToday>("/energy-availability/today");
  return data;
}

/* -------------------------------------------------------------- constants */

/** Mirrors app/services/energy_availability.py:50-52. Source of truth is the
 *  service; these exist only to draw the lines and must be changed together. */
const EA_OPTIMAL = 45;
const EA_SUBCLINICAL = 30;
const EA_PROBLEMATIC = 25;

/** Chart ceiling. 60 keeps the 30-line at mid-height and leaves headroom for a
 *  genuinely well-fed day without flattening the low ones. */
const SCALE_MAX = 60;

const BAR_TONE: Record<EAStatus, string> = {
  optimal: "bg-navy",
  subclinical: "bg-taupe",
  problematic: "bg-amber",
  severe: "bg-coral",
};

type Verdict = "severe" | "chronic" | "insufficient" | "ok";

/* ------------------------------------------------------------- component */

export function EnergyAvailabilityCard() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [showMethod, setShowMethod] = useState(false);

  const windowQ = useQuery({
    queryKey: ["energy-availability", "window"],
    queryFn: getEnergyWindow,
    staleTime: 60_000,
  });

  // Only reason to call /today is ffm_kg for the methodology line, so it does
  // not load until someone asks how the number was made.
  const todayQ = useQuery({
    queryKey: ["energy-availability", "today"],
    queryFn: getEnergyToday,
    enabled: showMethod,
    staleTime: 60_000,
  });

  /* --- loading ---------------------------------------------------------- */
  if (windowQ.isLoading) {
    return (
      <Card>
        <SectionLabel>{t("energy.section_label", "Energy availability")}</SectionLabel>
        <div className="mt-3 space-y-3">
          <Skeleton
            className="h-10 w-32"
            label={t("energy.loading", "Loading energy availability")}
          />
          <Skeleton className="h-24 w-full" label={t("energy.loading", "Loading energy availability")} />
        </div>
      </Card>
    );
  }

  /* --- error ------------------------------------------------------------ */
  // TanStack v5 dropped the onError option on queries: the explicit isError
  // branch IS the error path. A card that renders nothing on failure is the
  // silent-failure defect, not an acceptable fallback.
  if (windowQ.isError || !windowQ.data) {
    return (
      <Card>
        <SectionLabel>{t("energy.section_label", "Energy availability")}</SectionLabel>
        <div className="mt-3">
          <EmptyState
            variant="inline"
            title={t("energy.load_failed_title", "Could not load energy availability")}
            description={t(
              "energy.load_failed_desc",
              "The last 7 days did not come back from the server.",
            )}
          />
          <Button
            variant="ghost"
            size="sm"
            className="mt-3"
            onClick={() => void windowQ.refetch()}
            loading={windowQ.isFetching}
          >
            <RefreshCw className="h-3.5 w-3.5" strokeWidth={2} />
            {t("energy.retry", "Try again")}
          </Button>
        </div>
      </Card>
    );
  }

  const w = windowQ.data;

  /* --- empty ------------------------------------------------------------ */
  // Nothing logged in 7 days. EA cannot be estimated from an empty diary, and
  // the honest thing is to say so rather than draw a chart of zeroes — a day
  // with no meals reads as EA = −exercise/FFM, which would look catastrophic.
  if (w.days_with_data === 0) {
    return (
      <Card>
        <div className="flex items-center justify-between">
          <SectionLabel>{t("energy.section_label", "Energy availability")}</SectionLabel>
          <MethodologyToggle open={showMethod} onToggle={() => setShowMethod((v) => !v)} />
        </div>

        <EmptyState
          variant="hero"
          icon={Utensils}
          title={t("energy.empty_title", "No meals logged yet")}
          description={t(
            "energy.empty_desc",
            "Energy availability compares what you ate with what you burned training. Log a few days of meals and the last 7 days will appear here.",
          )}
        >
          <Button size="sm" onClick={() => nav("/nutrition")}>
            {t("energy.empty_cta", "Log a meal")}
          </Button>
        </EmptyState>

        {showMethod ? <Methodology todayQ={todayQ} /> : null}
      </Card>
    );
  }

  /* --- populated -------------------------------------------------------- */
  const verdict: Verdict = w.insufficient_data
    ? "insufficient"
    : w.severe_low_ea
      ? "severe"
      : w.chronic_low_ea
        ? "chronic"
        : "ok";

  // readings[0] is today; the chart reads left-to-right, oldest first.
  const ordered = [...w.readings].reverse();
  const avg = w.rolling_7d_avg;
  const avgTone =
    avg === null
      ? "text-taupe"
      : avg < EA_PROBLEMATIC
        ? "text-coral"
        : avg < EA_SUBCLINICAL
          ? "text-amber-dark"
          : "text-navy-deep";

  return (
    <Card>
      <div className="flex items-center justify-between">
        <SectionLabel>{t("energy.section_label", "Energy availability")}</SectionLabel>
        <MethodologyToggle open={showMethod} onToggle={() => setShowMethod((v) => !v)} />
      </div>

      {/* Headline: the 7-day average, with the 3-day as the leading edge. */}
      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <div className="flex items-baseline gap-2">
            <span className={cn("metric-number text-[34px] leading-none", avgTone)}>
              {avg === null ? "—" : avg.toFixed(1)}
            </span>
            <span className="text-[11px] text-taupe">
              {t("energy.unit", "kcal / kg FFM")}
            </span>
          </div>
          <div className="mt-1 text-[11px] text-taupe">
            {t("energy.avg_7d_caption", "7-day average across {{n}} logged days", {
              n: w.days_with_data,
            })}
          </div>
        </div>

        <div className="text-right">
          <div className="section-label">{t("energy.avg_3d_label", "Last 3 days")}</div>
          <div className="metric-number text-[18px] text-navy-deep">
            {w.rolling_3d_avg === null ? "—" : w.rolling_3d_avg.toFixed(1)}
          </div>
        </div>
      </div>

      {/* 7-day chart against the 30 line the service actually flags on. */}
      <div className="mt-4">
        {/* Threshold lines are painted after the bars so the 30-line stays
            visible across a tall bar rather than disappearing behind it. */}
        <div className="relative h-28 border-b border-cream-border">
          <div className="absolute inset-0 flex items-end gap-1.5">
            {ordered.map((r) => (
              <Bar key={r.date} reading={r} />
            ))}
          </div>

          <ThresholdLine value={EA_OPTIMAL} label={t("energy.line_optimal", "45 optimal")} muted />
          <ThresholdLine value={EA_SUBCLINICAL} label={t("energy.line_low", "30 low")} />
        </div>

        <div className="mt-1 flex gap-1.5">
          {ordered.map((r, i) => (
            <span
              key={r.date}
              className={cn(
                "flex-1 text-center text-[10px] capitalize",
                i === ordered.length - 1 ? "font-medium text-navy-deep" : "text-taupe",
              )}
            >
              {fmtDate(r.date, "EEEEEE")}
            </span>
          ))}
        </div>
      </div>

      {/* Counts — the raw material behind the verdict, so it can be checked. */}
      <div className="mt-3 grid grid-cols-3 gap-2 border-t border-cream-border pt-3 text-[11px]">
        <Count
          label={t("energy.count_below_30", "Days under 30")}
          value={w.days_below_30_in_last_7}
          tone={w.days_below_30_in_last_7 >= 3 ? "warn" : "plain"}
        />
        <Count
          label={t("energy.count_below_25", "Days under 25")}
          value={w.days_below_25_in_last_7}
          tone={w.days_below_25_in_last_7 >= 1 ? "danger" : "plain"}
        />
        <Count
          label={t("energy.count_logged", "Days logged")}
          value={t("energy.count_logged_value", "{{n}} of 7", { n: w.days_with_data })}
          tone="plain"
        />
      </div>

      <VerdictBlock verdict={verdict} w={w} />

      {/* Standing disclaimer. Present in every populated state, not only the
          alarming ones — the boundary of what this number can claim does not
          move with the number. */}
      <p className="mt-3 border-t border-cream-border pt-2 text-[11px] leading-relaxed text-taupe">
        {t(
          "energy.disclaimer",
          "Estimated from your logged meals, training load and body composition. This is information for a conversation with a doctor or sports dietitian — not a diagnosis.",
        )}
      </p>

      {showMethod ? <Methodology todayQ={todayQ} /> : null}
    </Card>
  );
}

/* ------------------------------------------------------------ sub-pieces */

function MethodologyToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-taupe hover:text-amber-dark"
    >
      <Info className="h-3.5 w-3.5" strokeWidth={1.8} />
      {open
        ? t("energy.hide_methodology", "Hide how this is measured")
        : t("energy.show_methodology", "How this is measured")}
    </button>
  );
}

function ThresholdLine({
  value,
  label,
  muted = false,
}: {
  value: number;
  label: string;
  muted?: boolean;
}) {
  return (
    // The rule itself is the positioned element, so its edge lands exactly on
    // the value. Wrapping it in a flex box that also held the label made the
    // box as tall as the label's line box and centred the rule inside it,
    // drawing every threshold ~3.6 EA units high.
    <div
      className={cn(
        "pointer-events-none absolute inset-x-0 h-0 border-t border-dashed",
        muted ? "border-cream-border" : "border-taupe/60",
      )}
      style={{ bottom: `${(value / SCALE_MAX) * 100}%` }}
      aria-hidden="true"
    >
      {/* Absolute, so the label cannot give the rule any height and shift it off
          the value it marks. Sits just above the line at the right edge, clear
          of the bars, which grow from the left. */}
      <span
        className={cn(
          "absolute -top-3 right-0 text-[9px] uppercase tracking-wider",
          muted ? "text-taupe/60" : "text-taupe",
        )}
      >
        {label}
      </span>
    </div>
  );
}

function Bar({ reading }: { reading: EAReading }) {
  const { t } = useTranslation();

  if (!reading.has_meal_data) {
    return (
      <div
        className="flex h-full flex-1 items-end"
        title={t("energy.bar_no_data", "{{date}} — no meals logged", {
          date: fmtDate(reading.date),
        })}
        aria-label={t("energy.bar_no_data", "{{date}} — no meals logged", {
          date: fmtDate(reading.date),
        })}
      >
        <div className="h-1.5 w-full rounded-sm border border-dashed border-cream-border bg-cream" />
      </div>
    );
  }

  // EA can be negative (trained hard, ate little). Clamp for drawing only —
  // the number in the tooltip stays truthful.
  const pct = Math.max(2, Math.min(100, (reading.ea_kcal_per_kg_ffm / SCALE_MAX) * 100));
  const desc = t("energy.bar_value", "{{date}} — EA {{ea}}, ate {{intake}} kcal, burned {{burn}} kcal", {
    date: fmtDate(reading.date),
    ea: reading.ea_kcal_per_kg_ffm.toFixed(1),
    intake: Math.round(reading.intake_kcal),
    burn: Math.round(reading.exercise_kcal),
  });

  return (
    <div className="flex h-full flex-1 items-end" title={desc} aria-label={desc}>
      <div
        className={cn("w-full rounded-sm", BAR_TONE[reading.status])}
        style={{ height: `${pct}%` }}
      />
    </div>
  );
}

function Count({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone: "plain" | "warn" | "danger";
}) {
  return (
    <div className="rounded-md bg-cream px-2 py-2 text-center">
      <div className="text-[9px] uppercase tracking-wider text-taupe">{label}</div>
      <div
        className={cn(
          "metric-number mt-0.5 text-[18px]",
          tone === "danger"
            ? "text-coral"
            : tone === "warn"
              ? "text-amber-dark"
              : "text-navy-deep",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function VerdictBlock({ verdict, w }: { verdict: Verdict; w: EAWindow }) {
  const { t } = useTranslation();

  if (verdict === "ok") {
    return (
      <p className="mt-3 rounded-md bg-cream px-3 py-2 text-[12px] leading-relaxed text-navy-deep">
        {t(
          "energy.verdict_ok",
          "No sustained low days in the last 7. Intake is keeping up with training.",
        )}
      </p>
    );
  }

  if (verdict === "insufficient") {
    return (
      <p className="mt-3 rounded-md bg-cream px-3 py-2 text-[12px] leading-relaxed text-taupe">
        {t(
          "energy.verdict_insufficient",
          "Only {{n}} of the last 7 days have meal data. At least 3 are needed before this trend means anything — the low-energy flags stay off until then.",
          { n: w.days_with_data },
        )}
      </p>
    );
  }

  const severe = verdict === "severe";
  return (
    <div
      className={cn(
        "mt-3 flex items-start gap-2 rounded-md border px-3 py-2.5",
        severe ? "border-coral/40 bg-coral/5" : "border-amber/50 bg-amber/10",
      )}
      role="status"
    >
      <AlertTriangle
        className={cn("mt-0.5 h-4 w-4 shrink-0", severe ? "text-coral" : "text-amber-dark")}
        strokeWidth={1.8}
        aria-hidden="true"
      />
      <div className="text-[12px] leading-relaxed">
        <p className={cn("font-medium", severe ? "text-coral" : "text-navy-deep")}>
          {severe
            ? t("energy.verdict_severe_title", "Energy availability has been very low")
            : t("energy.verdict_chronic_title", "Energy availability has been low")}
        </p>
        <p className="mt-1 text-navy">
          {severe
            ? t(
                "energy.verdict_severe_desc",
                "{{low}} of your logged days fell below 30 kcal/kg FFM and {{veryLow}} below 25. Sustained low energy availability is the mechanism behind RED-S — bone density, thyroid and adaptation all sit downstream of it. Worth raising with a doctor or sports dietitian before the next build block.",
                { low: w.days_below_30_in_last_7, veryLow: w.days_below_25_in_last_7 },
              )
            : t(
                "energy.verdict_chronic_desc",
                "{{low}} of your logged days fell below 30 kcal/kg FFM. That pattern is associated with hormonal and bone-density effects, and it usually appears when eating a little less and training a little more happen in the same week. Eating more, or training less, closes the gap.",
                { low: w.days_below_30_in_last_7 },
              )}
        </p>
      </div>
    </div>
  );
}

/** How the number is made, plus the FFM divisor — the one figure only /today
 *  returns. Its own loading and error lines: this panel must not go blank. */
function Methodology({ todayQ }: { todayQ: UseQueryResult<EAToday> }) {
  const { t } = useTranslation();
  return (
    <div className="mt-3 rounded-md border border-cream-border bg-cream-light p-3 text-[11px] leading-relaxed text-taupe">
      <p className="text-navy-deep">
        {t(
          "energy.method_formula",
          "EA = (calories eaten − calories burned training) ÷ fat-free mass. It is not a calorie deficit: it is the energy left over for everything that is not exercise — hormones, bone remodelling, immunity, repair.",
        )}
      </p>

      <ul className="mt-2 space-y-0.5">
        <li>
          {t("energy.method_optimal", "{{n}} and above — optimal, full anabolic capacity", {
            n: EA_OPTIMAL,
          })}
        </li>
        <li>
          {t("energy.method_subclinical", "{{lo}}–{{hi}} — watch; hormonal markers may shift", {
            lo: EA_SUBCLINICAL,
            hi: EA_OPTIMAL - 1,
          })}
        </li>
        <li>
          {t("energy.method_problematic", "{{lo}}–{{hi}} — REDs risk", {
            lo: EA_PROBLEMATIC,
            hi: EA_SUBCLINICAL - 1,
          })}
        </li>
        <li>
          {t("energy.method_severe", "Below {{n}} — clinical concern", { n: EA_PROBLEMATIC })}
        </li>
      </ul>

      <p className="mt-2">
        {t(
          "energy.method_sources",
          "Thresholds follow the ACSM 2016 and IOC REDs 2023 consensus statements.",
        )}
      </p>

      <p className="mt-2">
        {todayQ.isLoading ? (
          t("energy.method_ffm_loading", "Loading the body-composition figure…")
        ) : todayQ.isError || !todayQ.data ? (
          <span className="text-coral">
            {t("energy.method_ffm_failed", "Could not load today's fat-free mass figure.")}
          </span>
        ) : (
          t(
            "energy.method_ffm",
            "Today's divisor: {{ffm}} kg fat-free mass. Where a body-composition reading exists it comes from that; otherwise it is estimated from your weight, so log a measurement if you want this ratio to be about you. Training burn is derived from workout TSS, so both sides are estimates.",
            { ffm: todayQ.data.ffm_kg.toFixed(1) },
          )
        )}
      </p>

      <p className="mt-2 inline-flex items-center gap-1">
        <Gauge className="h-3 w-3" strokeWidth={1.8} aria-hidden="true" />
        {t("energy.method_window", "The window is always the last 7 days in your own timezone.")}
      </p>
    </div>
  );
}
